import Logo from "~/components/Logo"
import {
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  bold,
  faqGrid,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h3Style,
  headerTextStyle,
  imagePreviewBodyStyle,
  infoWrap,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"
import {
  ActionBtn,
  Bold,
  GardenDesignGuideLink,
  GardenDesignGuideNav,
  H2,
  H3,
  P,
  Section,
} from "~/components/articleComponents"

const TITLE = "Cottage Garden Design Ideas Generated With AI"
const DESCRIPTION =
  "See how you can use Rescape AI's garden design tool to visualise your dream Cottage Garden."
const URL =
  "https://rescapeai.com/garden-design-guide/cottage/cottage-garden-ideas-using-ai"
const IMAGE = "https://rescapeai.com/guide/bc861264-b11a-4bf0-b2ce-edb0594bec081.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/guide/bc861264-b11a-4bf0-b2ce-edb0594bec081.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>Cottage Garden Design Ideas with AI</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Designing a cottage garden is all about charm, abundance, and a touch of
            whimsy. In the past, shaping this kind of space meant relying on a keen eye
            for informal beauty—layered blooms, meandering paths, and a natural sense of
            welcome that feels both nostalgic and alive. Now, tools like Rescape AI make
            it easy to play with those classic elements in a modern, low-effort way. With
            just a photo, you can try out planting schemes, path ideas, and cozy garden
            nooks, experimenting freely before you ever put a spade in the soil.
          </p>
        </section>
        <Section>
          <ActionBtn>Get Started Now</ActionBtn>
        </Section>
        <Section>
          <div className={infoWrap}>
            <figure>
              <div className={articleCompareImages}>
                <img
                  src={"/guide/garden-2-before-ai-design.webp"}
                  alt={"Garden before redesign"}
                  loading="lazy"
                />
                <img
                  src={"/guide/bc861264-b11a-4bf0-b2ce-edb0594bec081.webp"}
                  alt={"A cottage garden idea generated by AI"}
                  loading="lazy"
                />
              </div>
              <img
                className={articleImageStyle}
                src={"/guide/bc861264-b11a-4bf0-b2ce-edb0594bec081.webp"}
                alt={"cottage garden design idea"}
                loading="lazy"
                style={{
                  height: "320px",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "0% 80%",
                  marginTop: "2rem",
                }}
              />
              <figcaption className={figCaptionStyle}>
                A soft, sunlit cottage-style patio filled with blooming flowers and
                vintage white furniture, creating a cozy corner for quiet mornings or
                afternoon tea.
              </figcaption>
            </figure>
          </div>
          <H3>Here are the key features the AI introduced:</H3>
          <ul className={unorderedList}>
            <li>
              <Bold>Softened the space:</Bold> Replaced the hard paving with a lighter,
              pebbled surface to create a gentler, cottage-style feel.
            </li>
            <li>
              <Bold>Added vintage furniture:</Bold> Introduced a white, weathered table,
              chair, and bench with decorative details.
            </li>
            <li>
              <Bold>Brought in flowers and greenery:</Bold> Filled the area with potted
              blooms, climbing plants, and soft, natural-looking foliage.
            </li>
            <li>
              <Bold>Made the backdrop rustic:</Bold> Swapped the painted fence for a
              weathered wooden one and added a hanging planter for charm.
            </li>
          </ul>
        </Section>
        <Section>
          <div className={infoWrap}>
            <figure>
              <div className={articleCompareImages}>
                <img
                  src={"/guide/garden-1-before-ai-design.webp"}
                  alt={"Garden before redesign"}
                  loading="lazy"
                />
                <img
                  src={"/guide/cottage-garden-bench-2.webp"}
                  alt={"An AI cottage garden with a bench"}
                  loading="lazy"
                />
              </div>
              <img
                className={articleImageStyle}
                src={"/guide/cottage-garden-bench-2.webp"}
                alt={"cottage garden design idea with a bench"}
                loading="lazy"
                style={{
                  height: "320px",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "0% 80%",
                  marginTop: "2rem",
                }}
              />
              <figcaption className={figCaptionStyle}>
                Cottage Garden design idea generated on{" "}
                <Link href="/hello/signup">Rescape AI</Link> Style: 'Cottage', Features:
                'A comfy bench with flowers around it'. Creativity: 70%
              </figcaption>
            </figure>
          </div>
          <H3>Here are the key features the AI introduced:</H3>
          <ul className={unorderedList}>
            <li>
              <Bold>Added layered flower borders:</Bold> Introduced roses, colorful
              perennials, and soft cottage-garden flowers along the fence to break up the
              plain boundary.
            </li>
            <li>
              <Bold>Created a focal point:</Bold> Placed a wooden bench with a bouquet and
              throw blanket to add warmth and charm.
            </li>
            <li>
              <Bold>Softened the scene with </Bold>color and texture: Mixed pinks,
              purples, and wildflower tones to create a natural, romantic feel.
            </li>
            <li>
              <Bold>Enhanced the lighting: </Bold>Added a warm, dreamy glow to give the
              garden a calm, storybook atmosphere.
            </li>
          </ul>
        </Section>
        <Section>
          <div className={infoWrap}>
            <figure>
              <div className={articleCompareImages}>
                <img
                  src={"/guide/garden-5.webp"}
                  alt={"Garden before redesign"}
                  loading="lazy"
                />
                <img
                  src={"/guide/cottage-garden-winding-pathways-2.webp"}
                  alt={"Winding pathways through a small garden"}
                  loading="lazy"
                />
              </div>
              <img
                className={articleImageStyle}
                src={"/guide/cottage-garden-winding-pathways-2.webp"}
                alt={"Winding pathways through a small garden closeup"}
                loading="lazy"
                style={{
                  height: "320px",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "0% 80%",
                  marginTop: "2rem",
                }}
              />
              <figcaption className={figCaptionStyle}>
                Cottage Garden design idea generated on Rescape AI. Style: 'Cottage',
                Features: 'Winding pathways'. Creativity: 80%.
              </figcaption>
            </figure>
          </div>
          <H3>Here are the key features the AI introduced:</H3>
          <ul className={unorderedList}>
            <li>
              <Bold>Replaced the patchy lawn with planting beds:</Bold> The empty grass
              area was transformed into lush, layered borders filled with flowers and
              shrubs.
            </li>
            <li>
              <Bold>Added winding pathways:</Bold> Curving stone paths now guide you
              through the garden, creating flow and a classic cottage-style layout.
            </li>
            <li>
              <Bold>Introduced abundant color and texture:</Bold> A mix of blooms—purples,
              pinks, whites, yellows—adds richness and that full, romantic cottage feel.
            </li>
            <li>
              <Bold>Softened the structure:</Bold> Bushes, perennials, and climbing plants
              were added around edges and corners to make the whole space feel greener,
              softer, and more inviting.
            </li>
          </ul>
        </Section>
        <Section>
          <div className={infoWrap}>
            <figure>
              <div className={articleCompareImages}>
                <img
                  src={"/guide/garden-6.webp"}
                  alt={"Garden before redesign"}
                  loading="lazy"
                />
                <img
                  src={"/guide/cottage-garden-winding-pathways.webp"}
                  alt={"Winding pathways through a small garden"}
                  loading="lazy"
                />
              </div>
              <img
                className={articleImageStyle}
                src={"/guide/cottage-garden-winding-pathways.webp"}
                alt={"Winding pathways through a small garden closeup"}
                loading="lazy"
                style={{
                  height: "320px",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "0% 80%",
                  marginTop: "2rem",
                }}
              />
              <figcaption className={figCaptionStyle}>
                Cottage Garden design idea generated on Rescape AI.
              </figcaption>
            </figure>
          </div>
          <H3>Here are the key features the AI introduced:</H3>
          <ul className={unorderedList}>
            <li>
              <Bold>Replaced the bare paving with planting beds:</Bold> The dull, empty
              patio was transformed into rich, flower-filled borders.
            </li>
            <li>
              <Bold>Added a winding stone path:</Bold> A soft, curving walkway now leads
              through the space, giving it a classic cottage-garden feel.
            </li>
            <li>
              <Bold>Filled the area with abundant color:</Bold> Roses, salvias, and mixed
              perennials bring layers of pinks, purples, blues, and greens.
            </li>
            <li>
              <Bold>Softened the boundaries:</Bold> Climbing plants and lush shrubs were
              added along the fences to make the whole corner feel greener, fuller, and
              more inviting.
            </li>
          </ul>
        </Section>
        <Section>
          <div className={infoWrap}>
            <figure>
              <div className={articleCompareImages}>
                <img
                  src={"/guide/garden-3-before-ai-design.webp"}
                  alt={"Garden before redesign"}
                  loading="lazy"
                />
                <img
                  src={"/guide/cottage-garden-unstructured.webp"}
                  alt={"Unstructured cottage garden design from Rescape AI"}
                  loading="lazy"
                />
              </div>
              <img
                className={articleImageStyle}
                src={"/guide/cottage-garden-unstructured.webp"}
                alt={"Unstructured cottage garden design from Rescape AI"}
                loading="lazy"
                style={{
                  height: "320px",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "0% 80%",
                  marginTop: "2rem",
                }}
              />
              <figcaption className={figCaptionStyle}>
                Cottage Garden design idea generated on Rescape AI.
              </figcaption>
            </figure>
          </div>
          <H3>Here are the key features the AI introduced:</H3>
          <ul className={unorderedList}>
            <li>
              <Bold>Filled the open lawn with planting beds:</Bold> The plain grassy area
              was transformed into layered, colorful cottage-style borders.
            </li>
            <li>
              <Bold>Added meandering stone pathways:</Bold> Curved stepping-stone paths
              were introduced to create flow and make the garden feel more inviting.
            </li>
            <li>
              <Bold>Introduced abundant cottage blooms:</Bold> A mix of purples, pinks,
              blues, and whites now fills the space, giving it that soft, romantic look.
            </li>
            <li>
              <Bold>Softened the boundaries with greenery:</Bold> Bushes, climbing plants,
              and taller perennials were added along the fences to make the garden feel
              enclosed and lush.
            </li>
          </ul>
        </Section>
        <Section>
          <H3>Final Thoughts</H3>
          <P>
            Creating a cottage garden no longer requires guesswork or years of trial and
            error. With Rescape AI, you can quickly test layouts, planting ideas, and
            features using just a photo of your space. The examples above show how AI can
            turn plain lawns and patios into detailed cottage-style designs, complete with
            pathways, layered borders, and classic planting schemes.
          </P>
          <P>
            If you’re planning a garden refresh, this gives you a clear way to explore
            options, compare ideas, and decide what will work best before making any
            physical changes. It’s a simple, efficient tool for visualizing a cottage
            garden that fits your home and your goals.
          </P>
        </Section>
        <ActionBtn>Get Started Here</ActionBtn>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
